import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useRouter } from 'next/router';
import { Avatar, Button, Chip, Hidden, IconButton } from '@material-ui/core';
import clsx from 'clsx';

import { getBadgeIcon } from 'utils/helpers';
import { getRequest, putRequest } from 'utils/api';
import { updateProfile } from 'features/authSlice';
import useCurrentUser from 'hooks/useCurrentUser';
import useModalOpen from 'hooks/useModalOpen';
import { openModal } from 'features/utilsSlice';
import ConfirmedIcon from 'public/icons/confirmed.svg';
import CrownIcon from 'public/icons/crown/crown-purple.svg';
import EditIcon from 'public/icons/edit.svg';
import useProfileOwner from 'hooks/useProfileOwner';
import AddIcon from 'public/icons/add-white.svg';
import NoAvatarImage from 'public/images/no-avatar.svg';
import HandshakeIcon from 'public/icons/handshake-thick.svg';
import SendIcon from 'public/icons/send-white.svg';
import Info from './Info';
import InfluencerNetworks from './InfluencerNetworks';
import GigListDropdown from './GigListDropdown';
import AvatarInput from 'components/AvatarInput';
import SuccessGigDialog from 'components/gigs/SuccessGigDialog';
import SendMessageDialog from 'components/chat/SendMessageDialog';
import styles from 'styles/components/profile/Profile.module.scss';

const useStyles = makeStyles({
  large: {
    width: 108,
    height: 108,
    marginTop: '-54px',
    zIndex: 2,
    boxShadow: '0 3px 10px 0 rgba(0, 0, 0, 0.04), 0 1px 22px 0 rgba(0, 0, 0, 0.57)',
  },

  yellowBorder: {
    border: '2px solid #ffd700',
  },

  greenBorder: {
    border: '2px solid #12dfc7',
  },

  root: {
    height: 25,
  },

  label: {
    marginBottom: 2,
  },
});

const column = { flexDirection: 'column', alignItems: 'center' };
const avatarFullSize = { width: '100%', height: '100%' };

const ProfileInfo = ({
  profile,
  setProfile,
  isDialog,
  uniqInfluencerNetworks,
  profilePreview,
  handleInvite,
  fromMatches,
  hideInviteButton,
  gig,
}) => {
  const [preview, setPreview] = useState('');
  const [openGigs, setOpenGigs] = useState([]);
  const isSimpleProfileDialog = useModalOpen('Profile');
  const dispatch = useDispatch();
  const { asPath } = useRouter();
  const classes = useStyles();
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const fileRef = useRef();
  const profileConfirmed = profile.confirmed || profile.completed;
  const profileOwner = useProfileOwner(profile);
  const currentUser = useCurrentUser();
  const advertisersProfilePage = asPath.startsWith('/c/');
  const wishlistPage = asPath.includes('wishlist');
  const isPartner = profile?.partner;
  const showInvite =
    currentUser?.role === 'advertiser' &&
    openGigs.length > 0 &&
    !hideInviteButton &&
    !advertisersProfilePage &&
    !wishlistPage &&
    !isDialog;

  const showMessageButton =
    currentUser?.direct_message_seller === true &&
    !profileOwner &&
    !advertisersProfilePage;

  const handleOpenSendMessage = () => {
    dispatch(
      openModal({
        name: 'Send Message',
        data: { profileId: profile.id, profileName: profile.display_name },
      })
    );
  };

  const handleDrop = avatar => {
    const data = new FormData();

    data.set('profile[avatar]', avatar);

    const endpoint = `profiles/${profile.id}`;

    putRequest({
      endpoint,
      data,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    })
      .then(response => {
        dispatch(updateProfile(response.data));
        setProfile(response.data);
      })
      .catch(() => {});
  };

  const getClassesStyle = () => {
    if (profile.ambassador) {
      return classes.yellowBorder;
    } else if (!profileOwner && isPartner) {
      return classes.greenBorder;
    } else return '';
  };

  const getBadgeStyle = () => {
    if (profile.ambassador) {
      return (
        <Chip
          icon={<CrownIcon width={14} height={14} />}
          label="Ambassador"
          className={clsx(styles.chip, 'gold-chip')}
        />
      );
    } else if (!profileOwner && isPartner) {
      return (
        <Chip
          icon={<HandshakeIcon />}
          label="Partner"
          className={clsx(styles.chip, 'green-chip')}
        />
      );
    } else {
      return (
        <Chip icon={getBadgeIcon(profile.badge)} label={profile.badge} className={styles.chip} />
      );
    }
  };

  useEffect(() => {
    Promise.all([
      getRequest({ endpoint: 'gig_applications', query: { profile_id: profile.id } }),
      getRequest({ endpoint: 'gig_invitations', query: { profile_id: profile.id } }),
      getRequest({
        endpoint: 'gigs',
        query: { status: 'pending', public: true, per_page: 20, page: 1 },
      }),
    ])
      .then(([applications, invitations, gigs]) => {
        const ids = [...applications, ...invitations].map(item => item.gig.id);

        setOpenGigs(gigs.filter(gig => !ids.includes(gig.id)));
      })
      .catch(err => console.log('err', err));
  }, [profile.id]);

  return (
    <>
      <div
        className={clsx(
          styles.profileInfo,
          {
            [styles.noMarginTop]: profilePreview || !isLoggedIn || isDialog,
          },
          { [styles.previewDialog]: profilePreview }
        )}
      >
        <div className={styles.info}>
          <div
            className={styles.avatar}
            onClick={() => {
              if (isLoggedIn && profileOwner && !profilePreview) {
                fileRef.current.click();
              }
            }}
          >
            {profileConfirmed && <ConfirmedIcon className={styles.confirmedIcon} />}

            <Avatar
              src={profile?.avatar_url}
              className={clsx(classes.large, getClassesStyle())}
              alt="Cover"
            >
              {profileOwner ? '' : <NoAvatarImage style={avatarFullSize} />}
            </Avatar>

            {profile?.avatar_url && isLoggedIn && profileOwner && !profilePreview && (
              <IconButton className="icon-btn-hover-dark">
                <EditIcon width={24} height={24} alt="Edit" />
              </IconButton>
            )}

            {!profile.avatar_url && profileOwner && (
              <IconButton className="icon-btn-hover-dark">
                <AddIcon width={24} height={24} alt="Plus Sign" />
              </IconButton>
            )}

            {getBadgeStyle()}
          </div>

          <AvatarInput
            fileRef={fileRef}
            handleDrop={handleDrop}
            preview={preview}
            setPreview={setPreview}
            cropName="ProfileInfoCropDialog"
          />

          {isDialog && (
            <Hidden mdUp>
              <Info profile={profile} isDialog={isDialog} />
            </Hidden>
          )}

          {isSimpleProfileDialog && (
            <Hidden smDown>
              <Info profile={profile} isDialog={isDialog} />
            </Hidden>
          )}

          <Hidden smDown>
            <InfluencerNetworks profile={profile} influencerNetworks={uniqInfluencerNetworks} />
          </Hidden>
        </div>
      </div>

      {(showInvite || showMessageButton) && (
        <div className={styles.dropdownBtn} style={{ display: 'flex', gap: 10 }}>
          {showMessageButton && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSendMessage}
              startIcon={<SendIcon width={16} height={16} />}
            >
              Message
            </Button>
          )}
          {showInvite && (
            <GigListDropdown
              menuItems={openGigs}
              setMenuItems={setOpenGigs}
              handleClick={handleInvite}
              profile={profile}
              fromMatches={fromMatches}
              gig={gig}
              isDialog={isDialog}
            />
          )}
        </div>
      )}

      <SendMessageDialog />

      <SuccessGigDialog
        title="Awesome"
        subtitle="You invited the influencer to this gig!"
        description="Influencer will have up to 48 hours to accept or reject this gig invite. You will be notified when they respond."
        btnText="Ok Cool"
      />
    </>
  );
};

ProfileInfo.propTypes = {
  profile: PropTypes.object.isRequired,
  setProfile: PropTypes.func,
  uniqInfluencerNetworks: PropTypes.array,
  isDialog: PropTypes.bool,
  profilePreview: PropTypes.bool,
  gig: PropTypes.object,
  hideInviteButton: PropTypes.bool,
  handleInvite: PropTypes.func,
  fromMatches: PropTypes.bool,
};

ProfileInfo.defaultProps = {
  setProfile: () => {},
  uniqInfluencerNetworks: [],
  isDialog: false,
  profilePreview: false,
  gig: {},
  hideInviteButton: false,
  handleInvite: () => {},
  fromMatches: false,
};

export default ProfileInfo;
